import React, { useEffect } from 'react';
import cn from 'classnames';
import ScheduleDemoContext, { useScheduleDemoContext } from '@framework/ui/Organisms/ScheduleDemo/ScheduleDemoContext';
import ScheduleDemoForm from '@framework/ui/Organisms/ScheduleDemo/ScheduleDemoForm';
import ScheduleDemoFormSuccess from './ScheduleDemoFormSuccess';
import Modal from '@dealroadshow/uikit-modal';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import Image from '@framework/router/Next/Image';
import { convertSvgToDataUrl } from '@framework/data-helpers/formatters/convertSvgToDataUrl';

import successStamp from './assets/successStamp.inline.svg';
import scheduleDemoStamp from './assets/scheduleDemoStamp.inline.svg';
import styles from './scheduleDemoModal.module.scss';

interface IProps {
  tenant: string,
  // passed from Finsight's OurSolutions; BookDemoWrapper renders the modal without it,
  // which is why the component guards on `!!tenantName` below.
  tenantName?: string,
  // Neither call site passes both: BookDemoWrapper omits tenantName/afterClose, Finsight's
  // OurSolutions omits closeModal. `props: any` on the provider wrapper used to hide that.
  closeModal?: () => void,
  afterClose?: () => void,
}

const tenantColors: { [key: string]: string } = {
  allocate: '#5286d0',
  dealroadshow: '#5286d0',
  dealvdr: '#6fcf97',
  evercall: '#5acdbf',
  investorset: '#9a8356',
  loansend: '#5a5aab',
  researchroom: '#606c8b',
  verisend: '#92929d',
  '17g5': '#bf4f6b',
};

const ScheduleDemoModal = ({
  tenant,
  tenantName,
  closeModal,
  afterClose,
}: IProps) => {
  const {
    isSubmitting,
    isSubmitted,
    reset,
  } = useScheduleDemoContext();

  useEffect(reset, [tenant]);

  const stampLogoSrc = convertSvgToDataUrl(
    isSubmitted ? successStamp : scheduleDemoStamp,
    tenantColors[tenant],
  );

  return (
    <Modal
      hideOnOverlayClicked
      className={ cn(styles.scheduleDemoModal, `tenant-${ tenant }`) }
      isVisible
      onCloseClicked={ closeModal }
      afterClose={ afterClose }
      dataTest={ isSubmitted ? `${ tenant }ScheduleDemoModalSuccess` : `${ tenant }ScheduleDemoModal` }
      title={ (
        <div className={ styles.modalHeader }>
          <div className={ styles.modalHeaderContainer }>
            <div className={ styles.modalHeaderTitle }>{ isSubmitted ? 'Demo Scheduled' : 'Schedule a Demo' }</div>
            { !!tenantName && (<p className={ styles.tenantName }>for { tenantName }</p>) }
          </div>
          <div className={ styles.modalHeaderStamp }>
            <Image
              src={ stampLogoSrc }
              width="77"
              height="77"
              alt="Stamp Logo"
            />
          </div>
        </div>
      ) }
    >
      <Spinner
        isVisible={ isSubmitting }
        isFixed
        overlay
      />
      { isSubmitted
        ? <ScheduleDemoFormSuccess />
        : (
          <>
            <div className={ styles.modalContentContainer }>
              <p className={ styles.contentDescription }>
                It takes 15-30 minutes and we operate on your availability.<br />
                Our team will send a meeting planner within twenty minutes as confirmation.
              </p>
            </div>
            <ScheduleDemoForm tenant={ tenant } />
          </>
        ) }
    </Modal>
  );
};

export default (props: React.ComponentProps<typeof ScheduleDemoModal>) => (
  <ScheduleDemoContext>
    <ScheduleDemoModal { ...props } />
  </ScheduleDemoContext>
);
